import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormControl, ValidatorFn, AbstractControl } from '@angular/forms';
import { WebApiTransactionType, WebApiStrategyRunningType } from "../../../../utProto/webApiStrategyEnums_pb";
import { Router, ActivatedRoute, NavigationStart } from '@angular/router';
import {
    AlgoStrategy, StrategiesService, SearchSymbolResults,
    SymbolSearchService, QuoteStoreService, Quote, NgxToastrService,
    ExecuteAlgoRequest, RunningCompletedStrategy, MarketSocketService
} from '@shared/providers';
import { MarketIDMap, FEE_SCHEDULES_STRINGS } from '@app/_shared/shared.enums';
import { remove } from 'lodash-es';
import { Brokers } from '@app/modules/trading/modules/profile/components/brokers/interfaces/brokers.interfaces';
import { StrategyFormBuildMode, PRODUCT_TYPE, SAVE_ONLY_DEPLOY } from '../strategy-form/strategy.enums';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmationSuccessDialogComponent } from './components/confirmation-success-dialog/confirmation-success-dialog.component';
import { BROKERS } from '@app/_helpers';
import { Utils } from '@app/_helpers';
import { map, floor } from 'lodash';
import * as moment from 'moment';
import {
    MOMENT_DATE_FORMAT, MOMENT_TIME_FORMAT, DEFAULT_PRECISION, CURRENCY_LABEL,
    MOMENT_COMPLETE_TIME, MAX_PERCENTAGE, MAX_ABSOLUTE_VALUE, REG_EX, ANGULAR_EDITOR_CONFIG
} from '@app/_helpers/constants';
import { RouteMap } from '@app/_shared/shared.structs';
import { ControlErrorMatcher, ErrorMatcher } from '@app/app.models';
import { BrokersComponent } from '@app/modules/trading/modules/profile/components/brokers/brokers.component';
import { PersonalDetailsService } from '@app/modules/trading/modules/profile/providers/personal-details/personal-details.service';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import { DeployDialogComponent } from '../strategy-card/components/deploy-dialog/deploy-dialog.component';
import { Subscription } from 'rxjs';
import { BriefStrategyModel } from '@app/modules/trading/modules/market-place/providers/market-place/market-place.models';

@Component({
    selector: 'app-strategy-confirmation',
    templateUrl: './strategy-confirmation.component.html',
    styleUrls: ['./strategy-confirmation.component.scss']
})
export class StrategyConfirmationComponent implements OnInit, OnDestroy {
    strategy: AlgoStrategy & RunningCompletedStrategy;
    StrategyFormBuildMode = StrategyFormBuildMode;
    quotes: Quote[] = [];
    WebApiStrategyRunningType = WebApiStrategyRunningType;
    addedSymbols: SearchSymbolResults[] = [];
    CURRENCY_LABEL = CURRENCY_LABEL;
    matcher = new ControlErrorMatcher();
    errMatcher = new ErrorMatcher();
    brokers: Brokers[] = BROKERS;
    showEdit = true;
    update = false;

    private subscriptions = new Subscription();

    tags = [];
    separatorKeysCodes = [COMMA, ENTER];
    FEE_SCHEDULES_STRINGS = FEE_SCHEDULES_STRINGS;
    DEFAULT_PRECISION = DEFAULT_PRECISION;
    MAX_ABSOLUTE_VALUE = MAX_ABSOLUTE_VALUE;
    MAX_PERCENTAGE = MAX_PERCENTAGE;
    ANGULAR_EDITOR_CONFIG = ANGULAR_EDITOR_CONFIG;

    strategyForm = new UntypedFormGroup({
        algoName: new UntypedFormControl(null, [Validators.required, Validators.maxLength(40), Validators.pattern(REG_EX.emptySpaces), this.sameAlgoNameValidator.apply(this)]),
        algoNotes: new UntypedFormControl(null, []),
        minimumCapital: new UntypedFormControl(1, [Validators.required, Validators.min(1), Validators.max(MAX_ABSOLUTE_VALUE)]),
        public: new UntypedFormControl(null, [Validators.required]),
        monthlyFee: new UntypedFormControl(null, [Validators.required, Validators.min(0), Validators.max(MAX_ABSOLUTE_VALUE)]),
        feeSchedule: new UntypedFormControl(null, [Validators.required]),
        minimumMultiplier: new UntypedFormControl(null, [Validators.required, Validators.min(1), Validators.max(MAX_ABSOLUTE_VALUE)]),
        maximumMultiplier: new UntypedFormControl(null, [Validators.required, Validators.min(1), Validators.max(MAX_ABSOLUTE_VALUE)]),
        variableFee: new UntypedFormControl(null, [Validators.required, Validators.min(0), Validators.max(MAX_PERCENTAGE)]),
        strategyTagInput: new UntypedFormControl(null)
    });

    sameAlgoNameValidator(): ValidatorFn {
        return (formControl: AbstractControl): { [key: string]: boolean } | null => {
            const algoName = formControl.value;
            const isUnique = this.strategyService.isAlgoNameUniqueSync(algoName);
            /*
              If the algo name is not unique when the strategy is created or updated
            */
            const whenCreated = (!isUnique && !this.strategy?.id);
            const whenUpdated = (!isUnique && this.strategy?.id && this.strategy.algoName != algoName);
            return whenCreated || whenUpdated ? { sameAlgoName: true } : null;
        }
    }

    // Only Save Strategy And Do not run according to new implementation.
    modeOfExecutionMode = new UntypedFormGroup({
        marketType: new UntypedFormControl(SAVE_ONLY_DEPLOY, [Validators.required])
    })

    constructor(
        private quoteStoreService: QuoteStoreService,
        private router: Router,
        private strategyService: StrategiesService,
        private snackbar: NgxToastrService,
        private symbolSearchService: SymbolSearchService,
        private dialog: MatDialog,
        private activatedRoute: ActivatedRoute,
        private profileService: PersonalDetailsService,
        private marketSocketService: MarketSocketService
    ) {

        if (this.activatedRoute.snapshot.queryParams?.comingFrom === RouteMap.completed) {
            this.showEdit = false;
        }

        // Popstate handled this way so that we dont have to strip the structure before passing
        this.subscriptions.add(
            router.events.subscribe((event: NavigationStart) => {
                // Edited state should be saved in both create and update form cases
                if (event.navigationTrigger === 'popstate') {
                    this.edit();
                }
            })
        );

        try {
            this.strategy = this.router.getCurrentNavigation().extras.state.algo;
            if (!this.strategy) {
                this.router.navigateByUrl('/trading/strategies/create');
                return
            }

            
            this.symbolSearchService.onLoad.subscribe(loaded => {
                if (loaded) {
                    // Removing undefined/removed symbols from the symbols list
                    this.addedSymbols = this.symbolSearchService.findScripsBySecurity(this.strategy.symbols)
                        .filter(symbols => symbols);

                    this.addedSymbols.forEach((symbol) => {
                        this.subscribeSymbol(symbol);
                    })
                }
            })
        } catch (error) {
            this.router.navigateByUrl('/trading/strategies/create');
        }
    }

    ngOnInit(): void {
        this.fillForm();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    subscribeSymbol(symbol: SearchSymbolResults) {
        this.quotes.push(this.quoteStoreService.getFeedObj(MarketIDMap[symbol.market_id], symbol.security_id));
        this.marketSocketService.subscribeSymbol({market_id:MarketIDMap[symbol.market_id], security_id:symbol.security_id});
    }

    unSubscribeSymbol(symbol: SearchSymbolResults) {
        remove(this.quotes, (q => q = this.quoteStoreService.getFeedObj(MarketIDMap[symbol.market_id], symbol.security_id)))
        this.marketSocketService.unSubscribeSymbol({market_id:MarketIDMap[symbol.market_id], security_id:symbol.security_id});
    }

    removeSymbol(symbol: SearchSymbolResults) {
        this.unSubscribeSymbol(symbol);
        this.addedSymbols.splice(this.addedSymbols.indexOf(symbol), 1);
        this.strategy.symbols = map(this.addedSymbols, (symbol) => {
            return {
                security_id: symbol.security_id,
                market_id: symbol.market_id
            }
        });
    }

    get algoName() {
        return this.strategyForm.get('algoName').value ||
            `MyAlgo_${moment().format(MOMENT_DATE_FORMAT + '-' + MOMENT_COMPLETE_TIME)}`;
    }

    deploy(isDeploy?: boolean) {
        if (this.strategyForm.invalid) {
            return;
        }
        const data = this.modeOfExecutionMode.value;
        const nameData = this.strategyForm.value;
        const oldName = this.strategy.algoName;
        this.strategy.algoNotes = nameData.algoNotes;

        /**
         * ==== TICKET
         * RA-321
         *
         * ==== Fix
         * Lots to be calculated from value everytime user runs an algo. (For Display purpose only).
         *
         * Calculated by dividing value by either LTP or close Price (which ever is present).
         * If None of them is present divide 1.
         */
        if (!this.strategy.isLot) {
            this.strategy.symbols.forEach((symbol, idx) => {
                const quoteObj = this.quoteStoreService.getFeedObj(+MarketIDMap[symbol.market_id], symbol.security_id);
                const divisor = (Utils.applyPrecision(quoteObj?.LTP || quoteObj?.closePrice, DEFAULT_PRECISION)) || 1;
                const symbolInfo = this.symbolSearchService.symbolInfo(`${MarketIDMap[symbol.market_id]}_${symbol.security_id}`) || [1];
                const lot_size = symbolInfo[0];
                this.strategy.valueConvertedLot[idx] = floor(this.strategy.orderQty / (divisor * lot_size));
            })
        }

        this.profileService.isModeAllowed(this.strategy.executionStyle).subscribe(isAllowed => {
            if (isAllowed) {
                let isUniqueName = true;
                if (this.activatedRoute.snapshot.queryParams?.comingFrom === RouteMap.completed) {
                    // If reactivating algo, do not update algo directly execute it. Because in reactivate
                    // one symbols is reactivated at a time. Which overrides saved algo symbols.
                    this.executeAlgo(data.marketType, this.strategy.symbols);
                    return;
                }
                this.strategy.algoName = this.algoName;

                // Update market place settings before saving.
                this.strategy.setMarketPlaceSettings({ ...this.strategyForm.getRawValue(), tags: this.tags });
                if (!this.strategy.id) {
                    if (!isUniqueName) {
                        this.snackbar.simpleCustomToaster("Cannot add two algos with same name");
                        return;
                    }
                    if (data.marketType != SAVE_ONLY_DEPLOY) {
                        this.strategyService.saveStrategy(this.strategy).subscribe(res => {
                            if (data.marketType == WebApiStrategyRunningType.WEBAPISTRATEGYRUNNINGTYPE_BACKTESTING) {
                                this.router.navigate(['/trading/backtest/detail/' + res.algo_id + '/',], { queryParams: { comingFrom: RouteMap.createAlgo } });
                            } else {
                                this.executeAlgo(data.marketType);
                            }
                        })
                    } else {
                        this.saveStrategy(isDeploy);
                    }
                } else { // Updates the algo if already exists
                    const strategyObject = this.strategy;

                    if (data.marketType != WebApiStrategyRunningType.WEBAPISTRATEGYRUNNINGTYPE_BACKTESTING
                        && this.strategy.strategyMode != StrategyFormBuildMode.WIZARD) {
                        if (this.strategy.productType == PRODUCT_TYPE.INTRADAY) {
                            strategyObject.startDate = moment().format(MOMENT_DATE_FORMAT);
                            strategyObject.closeDate = moment().format(MOMENT_DATE_FORMAT);
                            strategyObject.closeTimestamp = moment(strategyObject.closeDate + " " + strategyObject.closeTime,
                                MOMENT_DATE_FORMAT + ' ' + MOMENT_TIME_FORMAT).unix();
                            strategyObject.startTimestamp = moment(strategyObject.startDate + " " + strategyObject.startTime,
                                MOMENT_DATE_FORMAT + ' ' + MOMENT_TIME_FORMAT).unix();
                        } else {
                            strategyObject.closeDate = moment().format(MOMENT_DATE_FORMAT);
                            strategyObject.closeTimestamp = moment(strategyObject.closeDate + " " + strategyObject.closeTime,
                                MOMENT_DATE_FORMAT + ' ' + MOMENT_TIME_FORMAT).unix();
                        }
                    }

                    if (this.strategy.strategyMode == StrategyFormBuildMode.WIZARD && strategyObject.closeTimestamp < moment().unix()) {
                        this.snackbar.simpleCustomToaster("End time expired. Please modify time to rerun the algo.");
                        return;
                    }

                    if (data.marketType != SAVE_ONLY_DEPLOY) {
                        this.strategyService.updateStrategy(this.strategy.id, strategyObject, oldName).subscribe(res => {
                            this.update = true;
                            if (data.marketType == WebApiStrategyRunningType.WEBAPISTRATEGYRUNNINGTYPE_BACKTESTING) {
                                this.router.navigate(['/trading/backtest/detail/' + this.strategy.id + '/'], { queryParams: { comingFrom: RouteMap.createAlgo } });
                                return;
                            }
                            this.executeAlgo(data.marketType)
                        })
                    } else {
                        this.strategyService.updateStrategy(this.strategy.id, strategyObject, oldName).subscribe(res => {
                            this.update = true;
                            if (isDeploy) {
                                this.openDeployDialog(this.strategy.id);
                            } else {
                                this.openConfirmationDialog();
                            }
                        })
                    }
                }
            } else {
                this.snackbar.simpleCustomToaster(`${this.strategy.executionStyle} not allowed in your plan`);
            }
        })
    }

    runBackTest() {
        this.modeOfExecutionMode.get('marketType').setValue(WebApiStrategyRunningType.WEBAPISTRATEGYRUNNINGTYPE_BACKTESTING);
        this.deploy();
    }

    fillForm() {
        Object.keys(this.strategyForm.controls).forEach(key => {
            let value = this.strategy[key];
            this.strategyForm.controls[key].setValue(value);
        });
        if (this.strategy.tags?.length) {
            this.tags = this.strategy.tags;
        }
        if (this.activatedRoute.snapshot.queryParams?.comingFrom === RouteMap.completed) {
            // in case of reactive disable algo name to be changed.
            this.strategyForm.get('algoName').disable();
        } else {
            this.strategyForm.get('algoName').enable();
        }
    }

    edit() {
        // If strategy already exists then it must have an id. If strategy id exists then open strategy form in edit mode.
        if (this.strategy.id) {
            this.router.navigate(['/trading/strategies/edit', this.strategy.id], {
                state: {
                    edit: this.strategy
                }
            })
        } else {
            this.router.navigate(['/trading/strategies/create'], {
                state: {
                    edit: this.strategy
                }
            })
        }

    }

    get isSaveOnly() {
        const data = this.modeOfExecutionMode.value;
        return data.marketType === SAVE_ONLY_DEPLOY;
    }

    saveStrategy(isDeploy?: boolean) {
        // strategy can't be deployed before saving
        // first save strategy and then deploy
        this.strategyService.saveStrategy(this.strategy).subscribe(res => {
            this.strategy.id = res.algo_id[0];
            if (isDeploy) {
                this.openDeployDialog(res.algo_id[0]);
            }
            else {
                this.openConfirmationDialog();
            }
        })
    }

    openDeployDialog(algoId: string) {
        this.strategyService.getStrategy(algoId, true).subscribe((response) => {
            const strategyData = new BriefStrategyModel(response as any);
            strategyData.convertFromSingleStrategyResponse(response as any);
            
            const dialogRef = this.dialog.open(DeployDialogComponent,
            {
                data: {
                    strategy: strategyData
                },
                panelClass: 'ut-pro-dialog-box-custom-header',
            });
          
            // If user closes deploy dialog (weather deploys or not) we will redirect to created strategies
            // otherwise user will stay on confirmation page and again press save.
            dialogRef.afterClosed().subscribe(() => {
              this.router.navigateByUrl('/trading/my-strategies');
            });
        })
    }

    openConfirmationDialog() {
        const dialogRef = this.dialog.open(ConfirmationSuccessDialogComponent, {
            panelClass: 'dialog-no-padding',
            width: '340px',
            height: '400px',
            data: {
                algo: this.strategy,
                algoName: this.strategy.algoName,
                runningType: this.modeOfExecutionMode.value.marketType,
                update: this.update
            }
        })
        dialogRef.afterClosed().subscribe((route: RouteMap) => {
            // If PnL(Positions Page) is clicked, custom routing will override this routing
            if (!route) {
                this.router.navigateByUrl('/trading/dashboard');
            }
        });
    }

    addTag(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();

        // Add our tag
        if (value) {
            this.tags.push(value);
            this.strategyForm.get('strategyTagInput').reset();
        }
    }

    removeTag(tag: string): void {
        const index = this.tags.indexOf(tag);

        if (index >= 0) {
            this.tags.splice(index, 1);
        }
    }

    executeAlgo(runningType: number, symbolOverride?: any[]) {
        const req: ExecuteAlgoRequest = {
            algo_type: runningType,
            state: WebApiTransactionType.WEBAPITRANSACTIONTYPE_NEW,
            algo_obj: this.strategy
        }

        if (symbolOverride) {
            req.symbols = symbolOverride;
        }
        this.strategyService.executeAlgo(req).subscribe(execRes => {
            // Confirmation Dialog should be opened only once
            if (execRes && execRes.length) {
                this.openConfirmationDialog();
                // RA-886 Only one message ('Algo Request Sent') should be displayed

                // Update: RA-963 No snackbar is required, ('Algo Request Sent') can be shown in
                // Confirmation Dialog
                // this.snackbar.open(execRes[0].message);
            }
        })
    }

    openBrokerModal() {
        this.dialog.open(BrokersComponent, {
            minWidth: 600,
            data: true
        });
    }
}
